import type { OnboardingStepKey } from '../../config/onboardingRegistry'
import {
  nextGuidedStepKey,
  ONBOARDING_GUIDED_SEQUENCE,
} from './onboardingGuided'
import { isOfferMgmtHubStep, previousGuidedStep } from './onboardingOfferManagementGating'
import {
  isOnboardingGuideCoreComplete,
  isOnboardingGuideFinished,
  isOptionalGuideStep,
  isPostGuideStep,
} from './onboardingGuideConfig'

/** Przewodnik trwa — checklista widoczna, bez rezygnacji i bez ukończenia całości. */
export function isOnboardingJourneyActive(
  status?: {
    visible?: boolean
    forfeited?: boolean
    all_completed?: boolean
  } | null,
  completedKeys?: Set<OnboardingStepKey>,
): boolean {
  if (!status?.visible || status.forfeited) return false
  if (isOnboardingGuideFinished(status, completedKeys ?? new Set())) return false
  return true
}

/** Moduł-hub przewodnika (bulk actions). */
export const ONBOARDING_HUB_MODULE_ID = 'allegro_zarzadzanie_ofertami'

/** Moduły zablokowane przez cały przewodnik podstawowy (nie są krokami checklisty). */
export const ONBOARDING_LOCKED_UNTIL_GUIDE_COMPLETE_MODULES = new Set<string>([
  'allegro_wielopaki',
  'allegro_kompleksowe',
  'allegro_wystawianie_od_zera',
])

/** Mapowanie modułów sidebar → krok onboardingu. */
export const ONBOARDING_MODULE_STEP: Partial<Record<string, OnboardingStepKey>> = {
  allegro_tytuly: 'titles_flow',
  allegro_tytuly_v2: 'titles_flow',
  allegro_opisomat: 'descriptions_flow',
  allegro_grafikomat: 'grafikomat_flow',
  allegro_parametry: 'parameters_flow',
  allegro_zestawy_elastyczne: 'create_bundle',
  allegro_kopiowanie_ofert: 'copy_offer',
}

export interface SidebarModuleLockState {
  locked: boolean
  /** Krok, po którego ukończeniu moduł się odblokuje (dla etykiety). */
  unlockAfterStep: OnboardingStepKey | null
  /** Bieżący krok przewodnika — dla już miniętych modułów. */
  currentStep: OnboardingStepKey | null
  /** Moduł spoza przewodnika podstawowego (zestawy, kopiowanie). */
  postGuideLock?: boolean
}

function stepIndex(stepKey: OnboardingStepKey): number {
  return ONBOARDING_GUIDED_SEQUENCE.indexOf(stepKey)
}

/**
 * Shared unlock rules for sidebar modules.
 * Unlocked when journey is inactive, step is completed, or step is current/next.
 */
export function getOnboardingStepLockState(
  stepKey: OnboardingStepKey,
  journeyLocksActive: boolean,
  completedKeys: Set<OnboardingStepKey>,
  currentGuidedStep: OnboardingStepKey | null,
): Pick<SidebarModuleLockState, 'locked' | 'unlockAfterStep' | 'postGuideLock'> {
  if (!journeyLocksActive) {
    return { locked: false, unlockAfterStep: null }
  }

  if (
    (isPostGuideStep(stepKey) || isOptionalGuideStep(stepKey)) &&
    !isOnboardingGuideCoreComplete(completedKeys)
  ) {
    return { locked: true, unlockAfterStep: null, postGuideLock: true }
  }

  if (completedKeys.has(stepKey)) {
    return { locked: false, unlockAfterStep: null }
  }

  const nextStep = nextGuidedStepKey(completedKeys)
  if (stepKey === nextStep || stepKey === currentGuidedStep) {
    return { locked: false, unlockAfterStep: null }
  }

  return {
    locked: true,
    unlockAfterStep: previousGuidedStep(stepKey),
  }
}

export function isOnboardingStepUnlocked(
  stepKey: OnboardingStepKey,
  journeyLocksActive: boolean,
  completedKeys: Set<OnboardingStepKey>,
  currentGuidedStep: OnboardingStepKey | null,
): boolean {
  return !getOnboardingStepLockState(
    stepKey,
    journeyLocksActive,
    completedKeys,
    currentGuidedStep,
  ).locked
}

export function getSidebarModuleLockState(
  moduleId: string,
  journeyLocksActive: boolean,
  completedKeys: Set<OnboardingStepKey>,
  currentGuidedStep: OnboardingStepKey | null,
): SidebarModuleLockState {
  const base = { locked: false, unlockAfterStep: null, currentStep: currentGuidedStep }

  if (!journeyLocksActive) return base

  // Hub ofert — zablokowany do czasu podłączenia Allegro; potem otwarty na kroki przewodnika.
  if (moduleId === ONBOARDING_HUB_MODULE_ID) {
    if (!completedKeys.has('connect_allegro')) {
      return {
        locked: true,
        unlockAfterStep: 'connect_allegro',
        currentStep: currentGuidedStep,
      }
    }
    return base
  }

  if (ONBOARDING_LOCKED_UNTIL_GUIDE_COMPLETE_MODULES.has(moduleId)) {
    return {
      locked: true,
      unlockAfterStep: null,
      currentStep: currentGuidedStep,
      postGuideLock: true,
    }
  }

  const moduleStep = ONBOARDING_MODULE_STEP[moduleId]
  if (!moduleStep) return base

  const stepLock = getOnboardingStepLockState(
    moduleStep,
    journeyLocksActive,
    completedKeys,
    currentGuidedStep,
  )
  return {
    locked: stepLock.locked,
    unlockAfterStep: stepLock.unlockAfterStep,
    currentStep: currentGuidedStep,
    postGuideLock: stepLock.postGuideLock,
  }
}

export function getBulkActionLockHint(
  actionStep: OnboardingStepKey,
  journeyLocksActive: boolean,
  currentStep: OnboardingStepKey | null,
  completedKeys: Set<OnboardingStepKey>,
): 'future' | 'past' | 'misc' | null {
  if (!journeyLocksActive || !currentStep) return null
  if (actionStep === currentStep) return null

  const actionIdx = stepIndex(actionStep)
  const currentIdx = stepIndex(currentStep)
  if (actionIdx < 0 || currentIdx < 0) return 'misc'

  if (actionIdx < currentIdx || completedKeys.has(actionStep)) return 'past'
  if (actionIdx > currentIdx) return 'future'
  return 'misc'
}

export function isHubStepActive(currentStep: OnboardingStepKey | null): boolean {
  return Boolean(currentStep && isOfferMgmtHubStep(currentStep))
}
